import {
    type ChangeEvent,
    type Dispatch,
    type FC,
    type ReactNode,
    type SetStateAction,
    useEffect,
    useState,
} from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';

import { Input } from 'dibs-elements/exports/Input';

import InputWrapper from './InputWrapper';

import {
    PREFIX_HTTPS,
    PREFIX_WWW,
    type SocialDetails,
    type SocialNetworkName,
    socialNetworksConfig,
} from './socialNetworksHelpers';

import styles from './SocialNetworks.scss';

const SOCIAL_DETAILS = 'SOCIAL_DETAILS';

const WEBSITE_REGEX =
    /^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9-]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/;

// removes empty string values
const getPathSegments = (url: string = ''): string[] => {
    const urlInfo = new URL(url.trim());
    return urlInfo.pathname.split('/').filter(Boolean);
};

const isValidURL = (url: string, baseUrls: readonly string[]): boolean => {
    for (const baseUrl of baseUrls) {
        const baseWithPrefixes = `${PREFIX_HTTPS}${PREFIX_WWW}${baseUrl}`;
        const baseWithoutProtocol = `${PREFIX_WWW}${baseUrl}`;
        if (
            url.startsWith(baseUrl) ||
            url.startsWith(baseWithPrefixes) ||
            url.startsWith(baseWithoutProtocol)
        ) {
            return true;
        }
    }
    return false;
};

const appendPrefixes = (url: string): string => {
    if (!url.startsWith(PREFIX_HTTPS) && !url.startsWith(PREFIX_WWW)) {
        return `${PREFIX_HTTPS}${PREFIX_WWW}${url}`;
    } else if (url.startsWith(PREFIX_WWW)) {
        return `${PREFIX_HTTPS}${url}`;
    }
    return url;
};

const getSocialNetworkInfo = (
    networkName: SocialNetworkName,
    url: string
): { isValid: boolean; name: string | null } => {
    switch (networkName) {
        case 'Instagram': {
            const config = socialNetworksConfig[networkName];
            const { baseUrls, validSegmentsLength } = config;
            if (!isValidURL(url, baseUrls)) {
                return { isValid: false, name: null };
            }
            url = appendPrefixes(url);
            const parts = getPathSegments(url);
            const isValid = parts.length === validSegmentsLength[0];
            const name = (isValid && parts.pop()) || null;
            return {
                isValid,
                name,
            };
        }
        case 'Facebook': {
            const config = socialNetworksConfig[networkName];
            const { baseUrls, validSegmentsLength } = config;
            if (!isValidURL(url, baseUrls)) {
                return { isValid: false, name: null };
            }
            url = appendPrefixes(url);
            const parts = getPathSegments(url);
            const isValid =
                parts.length === validSegmentsLength[0] || parts.length === validSegmentsLength[1];
            const name = (isValid && parts.pop()) || null;
            return {
                isValid,
                name,
            };
        }
        case 'Website': {
            const isUrl = WEBSITE_REGEX.test(url);
            return isUrl ? { isValid: true, name: url } : { isValid: false, name: null };
        }
        default: {
            return { isValid: false, name: null };
        }
    }
};

type TSocialInputProps = {
    network: SocialNetworkName;
    setSocialDetailsList: Dispatch<SetStateAction<SocialDetails[] | null>>;
};

const SocialInput: FC<TSocialInputProps> = ({ network, setSocialDetailsList }) => {
    const initialData = {
        network,
        socialUrl: '',
        socialUserName: '',
    };

    const [input, setInput] = useState<typeof initialData>(initialData);
    const [hasError, setHasError] = useState(false);

    return (
        <InputWrapper>
            <Input
                dataTn="social-media-username-input"
                hasAnimatedPlaceholder
                onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
                    setHasError(false);

                    const { isValid, name } = getSocialNetworkInfo(network, value);

                    if (value) {
                        setHasError(!isValid);
                    }

                    setInput({
                        network,
                        socialUrl: value,
                        socialUserName: name || '',
                    });
                }}
                onBlur={(): void => {
                    setSocialDetailsList(prevState => {
                        const filteredState = prevState?.filter(s => s.network !== network) || [];
                        const isInputValid = input.socialUrl && !hasError;
                        return isInputValid ? [...filteredState, input] : filteredState;
                    });
                }}
                placeholder={network}
                value={input.socialUrl}
                type="text"
                size="large"
                hasError={hasError}
            />
            {hasError && (
                <div className={styles.socialsError}>
                    <FormattedMessage
                        id="SocialNetworks.urlError"
                        defaultMessage="Please enter a valid link. {example}"
                        values={{
                            example: (
                                <FormattedMessage
                                    id="SocialNetworks.example"
                                    defaultMessage="E.g.: {url}"
                                    values={{ url: socialNetworksConfig[network].exampleUrl }}
                                />
                            ),
                        }}
                    />
                </div>
            )}
        </InputWrapper>
    );
};

type SocialNetworksList = {
    socialInstructions?: ReactNode;
    updateForm: (field: string, value: SocialDetails[] | null) => void;
    networks: ('Facebook' | 'Instagram' | 'Website')[];
    showError?: boolean;
    errorMessage?: string | ReactNode;
};

export const SocialNetworksList: FC<SocialNetworksList> = ({
    socialInstructions = (
        <FormattedMessage
            id="SocialNetworks.add"
            defaultMessage="Add at least one social network"
        />
    ),
    updateForm,
    networks,
    showError,
    errorMessage,
}) => {
    const [socialDetailsList, setSocialDetailsList] = useState<SocialDetails[] | null>(null);

    useEffect(() => {
        updateForm(SOCIAL_DETAILS, socialDetailsList);
    }, [updateForm, socialDetailsList]);

    return (
        <div className={styles.outerWrapper}>
            <div className={classnames(styles.wrapper, { [styles.error]: showError })}>
                <p className={styles.socialInstructions}>{socialInstructions}</p>
                <div>
                    {networks.map(network => (
                        <SocialInput
                            key={network}
                            network={network}
                            setSocialDetailsList={setSocialDetailsList}
                        />
                    ))}
                </div>
            </div>
            {showError && <div className={styles.socialsError}>{errorMessage}</div>}
        </div>
    );
};
